// The idempotent customElements.define guard lives in index.html as an inline
// head script — see web/src/index.ts for the full rationale;
// identical shape here.

// `schmancy-splash-screen` is referenced from index.html, which the
// schmancyAutoImport vite plugin does not scan — it has to register before
// body parse or the splash flashes undefined.
import "@mhmo91/schmancy/splash-screen";

// One Firebase app for every client surface. @momo/firebase-client owns the
// init and the emulator wiring; this module chooses when to run it.
import "./firebase.ts";

import { translate, uiLanguageFromBcp47 } from "@momo/i18n";
import { organizationId, setLocale, uiLanguage } from "@momo/views";
import { html, render } from "lit";
import { workspaceId } from "./firebase.ts";

// The UI language is decided here, before the door or its no-organization
// notice renders, so both speak it from their first frame. Every shared
// `@momo/views/checkin` view reads it off this one engine rather than off
// `navigator`, since those views also mount on the operator console, where a
// staff member's stored choice is the answer; a door phone has no language
// picker, so the stored key is absent there and the device's own language
// decides.
// Blocked site storage throws on access; it must not stop the scanner booting.
let storedLanguage: string | null = null;
try {
	storedLanguage = localStorage.getItem("momo/language");
} catch {}
setLocale(
	storedLanguage === "de" || storedLanguage === "en"
		? storedLanguage
		: uiLanguageFromBcp47(navigator.language, "de"),
);

// The load-time address seeds the organization every scanner reader keys on.
organizationId.set(workspaceId || null);

if (workspaceId || location.pathname === "/") {
	// Device storage only clears another organization's selection; when the
	// browser blocks it there is nothing stored to clear, and the scanner boots.
	try {
		if (workspaceId) {
			const previousOrganization = localStorage.getItem("scanner/organization");
			if (previousOrganization !== workspaceId) {
				localStorage.removeItem("scanner/selected-event-id");
				sessionStorage.removeItem("auth/sign-flow");
			}
			localStorage.setItem("scanner/organization", workspaceId);
		}
	} catch {}
	await Promise.all([
		import("./door-labels/door-labels.sync.ts"),
		import("./event/event.sync.ts"),
		import("./manual-lookup/manual-lookup.sync.ts"),
		import("./manual-lookup/order-detail.sync.ts"),
		import("./session/session.sync.ts"),
		import("./settings/today.sync.ts"),
		import("./ticket-types/ticket-types.sync.ts"),
		import("./tickets/tickets.sync.ts"),
		import("./verdict/buyer.sync.ts"),
		import("./verdict/handover.sync.ts"),
		import("./verdict/verdict.sync.ts"),
		import("./app-index.view.ts"),
	]);
} else {
	const notice = document.createDocumentFragment();
	render(
		html`<schmancy-theme root scheme="dark">
			<main class="grid min-h-dvh grid-cols-1 place-content-center justify-items-center p-6">
				<schmancy-typography role="status" type="body" token="lg">${translate(uiLanguage(), "scanner.session.noOrganization")}</schmancy-typography>
			</main>
		</schmancy-theme>`,
		notice,
	);
	document.querySelector("app-index")?.replaceWith(notice);
	document
		.querySelector("schmancy-splash-screen")
		?.dispatchEvent(new Event("ready", { bubbles: true, composed: true }));
}

// DEV-ONLY Kevin bridge. import.meta.env.DEV is a Vite build-time constant, so
// this branch is dead-code-eliminated from any production bundle. The
// import.meta.glob indirection (rather than a plain dynamic import) is required
// because tsc resolves a dynamic import's specifier statically — TS2307 on
// every checkout where kevin.mount.ts is absent, which is any tree that does
// not carry it. The glob's record is simply empty there, and vite/client types
// it opaquely so tsc never tries to resolve './kevin.mount.ts' as a real
// module. Identical to web/src/index.ts.
if (import.meta.env.DEV) {
	void import.meta
		.glob("./kevin.mount.ts")
		["./kevin.mount.ts"]?.()
		.catch((err: unknown) => {
			// A swallowed failure here is indistinguishable from Kevin simply not
			// being present. Name it instead of guessing.
			console.warn("[kevin] mount failed to load", err);
		});
}

// The same summon as the storefront: only its listener loads until Kevin is asked for.
{
	const load = import.meta.glob("./kevin-summon.mount.ts")[
		"./kevin-summon.mount.ts"
	];
	if (load)
		load().catch((err: unknown) => {
			console.warn("[kevin] summon listener failed to load", err);
		});
}

// Shell-only service worker — caches the app shell,
// never data; see public/sw.js's own header for the full contract. Absent in
// dev (Vite's own dev server already serves the shell fast, and a dev-mode
// SW would fight Vite's HMR by caching stale module responses).
if (!import.meta.env.DEV && "serviceWorker" in navigator) {
	navigator.serviceWorker.register("/sw.js").catch((err: unknown) => {
		console.error("[scanner] service worker registration failed", err);
	});
}
